import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
  structuredData?: object | object[];
  takeaways?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  locale?: string;
  alternateLanguages?: Array<{ lang: string; url: string }>;
  robots?: string;
}

// Prevents admin-entered text from closing the <script> tag
const jsonLd = (data: object) =>
  JSON.stringify(data).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');

export function SEOHead({
  title,
  description,
  canonical,
  ogImage = 'https://uspconsultancy.com.np/usp.png',
  ogType = 'website',
  keywords,
  structuredData,
  takeaways,
  author = 'USP Consultancy',
  publishedTime,
  modifiedTime,
  locale = 'en_US',
  alternateLanguages,
  robots = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
}: SEOProps) {
  const baseUrl = 'https://uspconsultancy.com.np';
  const fullCanonical = canonical || `${baseUrl}${window.location.pathname}`;
  const fullTitle = title.includes('USP Consultancy') ? title : `${title} | USP Consultancy`;
  // Takeaways are machine-readable only (for AI answer engines), not rendered on the page
  const summary = takeaways?.map((t) => t.trim()).filter(Boolean) ?? [];
  const takeawaysSchema = summary.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        url: fullCanonical,
        name: fullTitle,
        description,
        abstract: summary.join(' '),
      }
    : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author} />
      <meta name="publisher" content="USP Consultancy" />
      <meta name="robots" content={robots} />
      {/* allow overriding for specific crawlers if needed */}
      <meta name="googlebot" content={robots} />
      <meta name="bingbot" content={robots} />

      {/* Geographic Targeting */}
      <meta name="geo.region" content="NP-BA" />
      <meta name="geo.placename" content="Kathmandu" />
      <meta name="geo.position" content="27.6915;85.2773" />
      <meta name="ICBM" content="27.6915, 85.2773" />

      {/* Language & Locale */}
      <meta property="og:locale" content={locale} />
      <html lang="en" />

      {/* Canonical URL - Critical for SEO */}
      <link rel="canonical" href={fullCanonical} />

      {/* Alternate Language Versions */}
      {alternateLanguages?.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}

      {/* Open Graph / Facebook - Enhanced */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="USP Consultancy" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      <meta property="article:author" content={author} />

      {/* Twitter Card - Enhanced */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={fullTitle} />
      <meta name="twitter:site" content="@USPConsultancy" />
      <meta name="twitter:creator" content="@USPConsultancy" />

      {/* Mobile App Links */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="USP Consultancy" />

      {/* Structured Data */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((data, index) => (
            <script key={index} type="application/ld+json">
              {jsonLd(data)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {jsonLd(structuredData)}
          </script>
        )
      )}
      {takeawaysSchema && (
        <script type="application/ld+json">{jsonLd(takeawaysSchema)}</script>
      )}
    </Helmet>
  );
}
